import { icmEquity, icmPressureBucket, riskPremium, } from '../strategy/icm';
/** Backstop: no non-finite value may reach a grade. Since ST2 taught `icmEquity` to place busted
 *  stacks, the old fully-paid NaN is gone and this should be unreachable - it stays as a tripwire. */
const assertFiniteIcm = (label, ...vals) => {
    for (const v of vals)
        if (!Number.isFinite(v))
            throw new RangeError(`${label}: ICM resolved to a non-finite value (${v}) - refusing to grade off it.`);
};
/**
 * Is a stage snapshot's field ICM-priceable? ICM prices a WHOLE live field: every remaining player's
 * chips, against the whole live ladder. A snapshot whose `stacks` are only a SAMPLE of `playersLeft`
 * (the DT `local_100` early snapshot lists 6 stacks with 60 players left) is not a field - running ICM
 * over it would silently answer the wrong question, pricing those 6 as if they were the last 6 alive.
 *
 * This SUPERSEDES ST1's "needs prizes < players" rule, which was the right guard for the wrong reason:
 * it caught the sampled snapshots only because a sample happens to also be fully-paid, and it rejected
 * real fully-paid fields (a 3-handed final table) along with them. The real predicate is field
 * completeness, not bust room. Deep early/middle play is chip-EV anyway, which is exactly when DT
 * samples - so nothing of value is lost by declining to price them.
 */
export const isIcmPriceable = (snap) => snap.stacks.length === snap.playersLeft;
/**
 * Bubble factor: how much more a chip lost costs than a chip won gains, in $EV terms - the canonical
 * ICMIZER/HRC quantity. `BF = (EV_fold − EV_lose) / (EV_win − EV_fold)`, both differences per the same
 * at-risk chips. BF = 1 in a pure chip-EV world (cash, or a flat pay structure with no ladder left);
 * BF > 1 whenever busting is punished (the bubble / a steep pay jump), so hero needs more raw equity
 * to call. Infinity in the degenerate case where winning the chips adds no $EV (already locked to the
 * top prize) - a spot you should never gamble.
 */
export const bubbleFactor = (spot) => {
    const { chipStacks, payouts, heroIndex, villainIndex, atRisk } = spot;
    const dead = spot.deadMoney ?? 0;
    const foldStacks = [...chipStacks];
    foldStacks[villainIndex] += dead;
    const winStacks = [...chipStacks];
    winStacks[heroIndex] += atRisk + dead;
    winStacks[villainIndex] -= atRisk;
    const loseStacks = [...chipStacks];
    loseStacks[heroIndex] -= atRisk;
    loseStacks[villainIndex] += atRisk + dead;
    const evFold = icmEquity(foldStacks, payouts)[heroIndex];
    const evWin = icmEquity(winStacks, payouts)[heroIndex];
    const evLose = icmEquity(loseStacks, payouts)[heroIndex];
    assertFiniteIcm('bubbleFactor', evFold, evWin, evLose);
    const gain = evWin - evFold; // $EV gained by winning the chips
    const loss = evFold - evLose; // $EV lost by busting the chips
    if (gain <= 0)
        return Number.POSITIVE_INFINITY;
    return loss / gain;
};
/** Compute both call thresholds for an all-in spot from S7's chip math + risk premium. */
export const callThresholds = (spot) => {
    const dead = spot.deadMoney ?? 0;
    const chipEquity = spot.atRisk / (2 * spot.atRisk + dead);
    const rp = riskPremium(spot);
    assertFiniteIcm('callThresholds', rp);
    return {
        chipEquity,
        icmEquity: chipEquity + rp,
        riskPremium: rp,
        pressure: icmPressureBucket(rp),
    };
};
/**
 * Resolve an all-in call under ICM. The action follows ICM (the tournament-correct standard): call iff
 * hero's equity clears the ICM-adjusted breakeven. `icmOverfold` flags the teaching case - a spot the
 * chips say to call that ICM says to fold - which is exactly the bubble/final-table pressure the
 * tournament rules and grading exist to teach.
 */
export const callAllInDecision = (equity, spot) => {
    const thresholds = callThresholds(spot);
    const chipEvCall = equity >= thresholds.chipEquity;
    const icmCall = equity >= thresholds.icmEquity;
    return {
        equity,
        chipEvCall,
        icmCall,
        action: icmCall ? 'call' : 'fold',
        icmOverfold: chipEvCall && !icmCall,
        thresholds,
        bubbleFactor: bubbleFactor(spot),
    };
};
// ── Structure-aware wrappers (riskPremium "by pay structure") ──────────────────────────────────────
/** Build an AllInSpot from a DT PayoutStructure stage snapshot: hero and villain are picked by their
 *  index within the snapshot's `stacks`, at risk for `atRisk` chips (default: the shorter stack's whole
 *  stack - a jam-for-stacks). Uses the snapshot's still-live prize ladder (`payoutsInPlay`).
 *
 *  Risk-premium / bubble-factor analysis on the resulting spot needs bust room - a snapshot with
 *  prizes < players (a bubble/near-money spot). A fully-paid snapshot (prizes ≥ players, e.g. the
 *  `early`/`finalTable` snapshots that model exactly the paid seats) busts a stack to 0 with a fully-
 *  paid field, which the icm-calculator returns as NaN; the ICM helpers throw loudly in that case. */
export const spotFromStage = (preset, stage, heroIndex, villainIndex, opts) => {
    const snap = preset.stages.find((s) => s.stage === stage);
    if (!snap)
        throw new RangeError(`${preset.id}: no ${stage} stage snapshot`);
    if (!isIcmPriceable(snap))
        throw new RangeError(`${preset.id}: the '${stage}' snapshot lists ${snap.stacks.length} stack(s) but ${snap.playersLeft} ` +
            `players are left - it SAMPLES the field rather than modelling it, and ICM prices a whole field. ` +
            `Price a stage whose snapshot is the full live field (a bubble/ITM/final-table spot); deep ` +
            `${stage} play is chip-EV anyway.`);
    const stacks = snap.stacks;
    if (heroIndex < 0 || heroIndex >= stacks.length)
        throw new RangeError(`heroIndex ${heroIndex} out of range`);
    if (villainIndex < 0 || villainIndex >= stacks.length)
        throw new RangeError(`villainIndex ${villainIndex} out of range`);
    const atRisk = opts?.atRisk ?? Math.min(stacks[heroIndex], stacks[villainIndex]);
    return {
        chipStacks: stacks,
        payouts: snap.payoutsInPlay,
        heroIndex,
        villainIndex,
        atRisk,
        deadMoney: opts?.deadMoney ?? 0,
    };
};
/** The risk premium + bubble factor + pressure bucket for a spot pulled from a pay structure's stage
 *  snapshot - "riskPremium by pay structure" made concrete. */
export const riskProfileForStage = (preset, stage, heroIndex, villainIndex, opts) => {
    const spot = spotFromStage(preset, stage, heroIndex, villainIndex, opts);
    const rp = riskPremium(spot);
    return { riskPremium: rp, bubbleFactor: bubbleFactor(spot), pressure: icmPressureBucket(rp) };
};
//# sourceMappingURL=icm.js.map